/* Allen-Bradley cross-reference pages — interactive layer.
 *
 * Progressive enhancement, strictly. Every control below is CREATED here and
 * inserted into markup that is already complete and correct without it: with
 * JavaScript off, every candidate row is visible and every row still carries
 * its own "Request a quote" link. Nothing here fetches anything — it all reads
 * the data-* attributes the proxy already rendered (see lib/xref-render.ts in
 * iac-parts-seo, which is the other half of that contract).
 *
 * Lives in the theme rather than in the proxy response for the same reason
 * xref.css does: `shopify theme dev` hot-reloads it, and it is cached by the
 * CDN across every one of the 18,640 part pages instead of being re-sent inline
 * with each one.
 *
 * What it adds:
 *   - a filter toolbar over the candidate list (in stock / fits footprint)
 *   - row selection, driving a comparison table and a multi-part quote
 *   - copy-to-clipboard on every catalogue number
 *   - a sticky quote bar once the closest-match card scrolls away
 */
(function () {
  'use strict';

  var root = document.querySelector('.xr');
  if (!root) return;

  var partNumber = root.getAttribute('data-part') || '';
  var quoteBase = root.getAttribute('data-quote-base') || '';
  // The quote app itself and the message it prefills, used to build a quote
  // URL for several selected rows at once. Absent on an older proxy response,
  // which is why every use falls back to the single-row link.
  var quotePage = root.getAttribute('data-quote-page') || '';
  var quoteNote = root.getAttribute('data-quote-note') || '';
  var rows = Array.prototype.slice.call(root.querySelectorAll('.xr-row'));

  /* ------------------------------------------------------------------ utils */

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'class') node.className = attrs[k];
        else if (k === 'text') node.textContent = attrs[k];
        else if (attrs[k] != null) node.setAttribute(k, attrs[k]);
      });
    }
    (children || []).forEach(function (c) {
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
  }

  function rowLabel(row) {
    return (row.getAttribute('data-brand') || '') + ' ' + (row.getAttribute('data-model') || '');
  }

  /* -------------------------------------------------------- copy to clipboard
   * Engineers reading this page are usually transcribing the number into an
   * ERP, a BOM or an email. Selecting monospace text by hand is the small,
   * constant friction of every parts site. */

  function copyButton(text, describedAs) {
    var btn = el('button', {
      type: 'button',
      class: 'xr-copy',
      'aria-label': 'Copy ' + describedAs + ' to clipboard',
      title: 'Copy part number'
    });
    btn.innerHTML =
      '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
      '<path d="M5.5 1.5h7a1 1 0 0 1 1 1v9M3.5 4.5h7a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1z" ' +
      'fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    btn.addEventListener('click', function () {
      var done = function (ok) {
        btn.classList.toggle('is-copied', ok);
        btn.classList.toggle('is-failed', !ok);
        announce(ok ? text + ' copied' : 'Could not copy ' + text);
        setTimeout(function () {
          btn.classList.remove('is-copied', 'is-failed');
        }, 1600);
      };
      // navigator.clipboard needs a secure context; the storefront is HTTPS,
      // but a local `shopify theme dev` origin may not be.
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function () { done(true); }, function () { done(false); });
      } else {
        done(legacyCopy(text));
      }
    });
    return btn;
  }

  function legacyCopy(text) {
    var ta = el('textarea', { class: 'xr-visually-hidden' });
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    var ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    document.body.removeChild(ta);
    return ok;
  }

  /* Screen-reader announcements for changes that are otherwise only visual. */
  var live = el('div', { class: 'xr-visually-hidden', role: 'status', 'aria-live': 'polite' });
  root.appendChild(live);
  function announce(msg) { live.textContent = msg; }

  /* ------------------------------------------------------------------ rows */

  var selected = [];

  rows.forEach(function (row, i) {
    var model = row.getAttribute('data-model') || '';
    var id = 'xr-pick-' + i;

    var box = el('input', { type: 'checkbox', class: 'xr-pick', id: id });
    box.setAttribute('aria-label', 'Select ' + rowLabel(row) + ' to compare or quote');
    box.addEventListener('change', function () {
      row.classList.toggle('is-picked', box.checked);
      selected = rows.filter(function (r) { return r.querySelector('.xr-pick').checked; });
      syncBar();
    });
    row.insertBefore(el('div', { class: 'xr-row__pick' }, [box]), row.firstChild);
    // The grid only widens to a checkbox column once one actually exists.
    if (row.parentNode && row.parentNode.classList) row.parentNode.classList.add('is-interactive');

    var idCell = row.querySelector('.xr-row__id');
    if (idCell && model) idCell.appendChild(copyButton(model, rowLabel(row)));
  });

  /* --------------------------------------------------------------- filtering
   * 46 rows over 11 brands is a lot to read when the real question is usually
   * "what can I actually get, and will it fit the panel I already have?".
   * Both filters answer exactly that, and both work on rows already in the
   * DOM — no request, no re-render. */

  var filters = { stock: false, fit: false };
  var alts = root.querySelector('.xr-alts');
  var countEl = null;

  if (alts && rows.length) {
    var toolbar = el('div', { class: 'xr-tools' });
    var group = el('div', { class: 'xr-tools__group', role: 'group', 'aria-label': 'Filter candidates' });

    group.appendChild(toggle('In stock', 'stock', 'Show only candidates Industrial Automation Co. can supply from stock'));
    group.appendChild(toggle('Fits original footprint', 'fit', 'Show only candidates verified to fit the original panel envelope'));

    countEl = el('p', { class: 'xr-tools__count', role: 'status', 'aria-live': 'polite' });

    toolbar.appendChild(group);
    toolbar.appendChild(countEl);

    var firstBrand = alts.querySelector('.xr-brand');
    if (firstBrand) alts.insertBefore(toolbar, firstBrand);
    else alts.appendChild(toolbar);

    applyFilters();
  }

  function toggle(label, key, title) {
    var btn = el('button', {
      type: 'button',
      class: 'xr-chip',
      'aria-pressed': 'false',
      title: title,
      text: label
    });
    btn.addEventListener('click', function () {
      filters[key] = !filters[key];
      btn.setAttribute('aria-pressed', String(filters[key]));
      btn.classList.toggle('is-on', filters[key]);
      applyFilters();
    });
    return btn;
  }

  function matches(row) {
    if (filters.stock && row.getAttribute('data-stock') !== '1') return false;
    if (filters.fit && row.getAttribute('data-fit') !== 'fits') return false;
    return true;
  }

  function applyFilters() {
    var shown = 0;
    rows.forEach(function (row) {
      var ok = matches(row);
      row.hidden = !ok;
      if (ok) shown++;
      // A hidden row must not stay in a selection the user can no longer see.
      if (!ok) {
        var box = row.querySelector('.xr-pick');
        if (box && box.checked) {
          box.checked = false;
          row.classList.remove('is-picked');
        }
      }
    });

    // Collapse brand groups that filtered down to nothing, so the page does
    // not show a heading claiming candidates above an empty list.
    Array.prototype.forEach.call(root.querySelectorAll('.xr-brand'), function (group) {
      var any = group.querySelector('.xr-row:not([hidden])');
      group.hidden = !any;
    });

    selected = rows.filter(function (r) { return r.querySelector('.xr-pick').checked; });
    syncBar();

    if (countEl) {
      countEl.textContent =
        shown === rows.length
          ? 'Showing all ' + rows.length + ' candidates'
          : 'Showing ' + shown + ' of ' + rows.length + ' candidates';
    }
    if (shown === 0) showEmpty(true);
    else showEmpty(false);
  }

  var emptyNote = null;
  function showEmpty(on) {
    if (on && !emptyNote) {
      emptyNote = el('p', {
        class: 'xr-tools__empty',
        text: 'No candidate matches both filters. Our engineers can still source a replacement — request a quote and we will confirm the specification.'
      });
      if (alts) alts.appendChild(emptyNote);
    } else if (!on && emptyNote) {
      emptyNote.parentNode.removeChild(emptyNote);
      emptyNote = null;
    }
  }

  /* ------------------------------------------------------------- comparison
   * Built from the selected rows only. An engineer choosing between three
   * drives is doing a spec-by-spec comparison on paper anyway; this is that
   * table, with the Allen-Bradley original as the reference column. */

  var comparePanel = null;

  function buildCompare() {
    if (!selected.length) return;
    closeCompare();

    var fields = [
      ['Power (HP)', 'hp'],
      ['Power (kW)', 'kw'],
      ['Rated current (A)', 'amps'],
      ['Duty', 'duty']
    ];

    var table = el('table', { class: 'xr-compare__table' });

    var head = el('tr', {}, [el('th', { scope: 'col', text: '' })]);
    head.appendChild(el('th', { scope: 'col', class: 'is-origin' }, [
      el('span', { class: 'xr-compare__brand', text: 'Allen-Bradley' }),
      el('span', { class: 'xr-compare__model', text: partNumber })
    ]));
    selected.forEach(function (row) {
      head.appendChild(el('th', { scope: 'col' }, [
        el('span', { class: 'xr-compare__brand', text: row.getAttribute('data-brand') || '' }),
        el('span', { class: 'xr-compare__model', text: row.getAttribute('data-model') || '' })
      ]));
    });
    table.appendChild(el('thead', {}, [head]));

    var body = el('tbody');
    fields.forEach(function (f) {
      var tr = el('tr', {}, [el('th', { scope: 'row', text: f[0] })]);
      tr.appendChild(el('td', { class: 'is-origin', text: root.getAttribute('data-part-' + f[1]) || '—' }));
      selected.forEach(function (row) {
        tr.appendChild(el('td', { text: row.getAttribute('data-' + f[1]) || '—' }));
      });
      body.appendChild(tr);
    });

    var fitRow = el('tr', {}, [el('th', { scope: 'row', text: 'Panel footprint' })]);
    fitRow.appendChild(el('td', { class: 'is-origin', text: 'original' }));
    selected.forEach(function (row) {
      var v = row.getAttribute('data-fit');
      fitRow.appendChild(el('td', {
        text: v === 'fits' ? 'Fits' : v === 'larger' ? 'Larger than original' : 'Not verified'
      }));
    });
    body.appendChild(fitRow);

    var stockRow = el('tr', {}, [el('th', { scope: 'row', text: 'Availability' })]);
    stockRow.appendChild(el('td', { class: 'is-origin', text: 'not sold by IAC' }));
    selected.forEach(function (row) {
      stockRow.appendChild(el('td', {
        text: row.getAttribute('data-stock') === '1' ? 'In stock' : 'Quoted to order'
      }));
    });
    body.appendChild(stockRow);

    table.appendChild(body);

    var close = el('button', { type: 'button', class: 'xr-compare__close', 'aria-label': 'Close comparison', text: '×' });
    close.addEventListener('click', function () {
      closeCompare();
      announce('Comparison closed');
    });

    comparePanel = el('section', { class: 'xr-compare', tabindex: '-1', 'aria-label': 'Comparison with ' + partNumber }, [
      el('div', { class: 'xr-compare__head' }, [
        el('h2', { text: 'Comparing ' + selected.length + ' candidate' + (selected.length === 1 ? '' : 's') + ' with the ' + partNumber }),
        close
      ]),
      el('div', { class: 'xr-compare__scroll' }, [table]),
      el('p', {
        class: 'xr-compare__note',
        text: '"Not verified" means official dimensions were unavailable for one side, not that the candidate is too large. Confirm control features, I/O and mounting against datasheets before substituting.'
      })
    ]);

    if (alts) alts.insertBefore(comparePanel, alts.querySelector('.xr-brand'));
    comparePanel.focus();
    comparePanel.scrollIntoView({ block: 'nearest' });
    announce('Comparing ' + selected.length + ' candidates');
  }

  function closeCompare() {
    if (comparePanel && comparePanel.parentNode) comparePanel.parentNode.removeChild(comparePanel);
    comparePanel = null;
  }

  /* ------------------------------------------------------------- quote bar
   * Two jobs in one element. With nothing selected it is the always-reachable
   * quote CTA on a page that is now long. With rows selected it becomes a
   * multi-part quote — replacement work is usually a list of parts, not one. */

  var bar = null;
  var barCount = null;
  var barQuote = null;
  var barCompare = null;
  var barClear = null;

  function buildBar() {
    barCount = el('span', { class: 'xr-bar__count' });
    barCompare = el('button', { type: 'button', class: 'xr-btn xr-btn--thin', text: 'Compare' });
    barCompare.addEventListener('click', buildCompare);

    barClear = el('button', { type: 'button', class: 'xr-bar__clear', text: 'Clear' });
    barClear.addEventListener('click', function () {
      rows.forEach(function (r) {
        var b = r.querySelector('.xr-pick');
        if (b) b.checked = false;
        r.classList.remove('is-picked');
      });
      selected = [];
      closeCompare();
      syncBar();
      announce('Selection cleared');
    });

    barQuote = el('a', { class: 'xr-btn xr-btn--primary', href: quoteBase });

    bar = el('div', { class: 'xr-bar', hidden: 'hidden' }, [
      el('div', { class: 'xr-bar__inner' }, [barCount, el('div', { class: 'xr-bar__actions' }, [barClear, barCompare, barQuote])])
    ]);
    document.body.appendChild(bar);
  }

  /**
   * Build the quote URL for the current selection.
   *
   * The quote page reads one line per `handle` (a store product) and per `q`
   * (free text), so a multi-row selection is just those parameters repeated —
   * the same split the proxy already made when it rendered each row's own
   * link. `note` carries the Allen-Bradley part being replaced; it is never a
   * line, because it is the one part we do not sell.
   */
  function quoteUrlFor(list) {
    if (!list.length) return quoteBase;
    if (list.length === 1) return list[0].getAttribute('data-quote') || quoteBase;
    if (!quotePage) return quoteBase;

    var params = ['ref=cross-reference'];
    list.forEach(function (row) {
      var handle = row.getAttribute('data-handle');
      params.push(handle
        ? 'handle=' + encodeURIComponent(handle)
        : 'q=' + encodeURIComponent(rowLabel(row)));
    });
    if (quoteNote) params.push('note=' + encodeURIComponent(quoteNote));
    return quotePage + '?' + params.join('&');
  }

  function syncBar() {
    if (!bar) return;
    var n = selected.length;

    // The bar says what is being quoted, and it is never the Allen-Bradley
    // part: we do not sell it. Naming it alone here read as an offer to quote
    // it, which is the opposite of what the page is for.
    barCount.textContent = n
      ? n + ' replacement' + (n === 1 ? '' : 's') + ' selected'
      : partNumber
        ? 'Replacement for Allen-Bradley ' + partNumber
        : 'Allen-Bradley cross-reference';

    barClear.hidden = !n;
    barCompare.hidden = !n;
    barQuote.textContent = n ? 'Quote ' + n + ' selected' : 'Quote a replacement';
    barQuote.href = quoteUrlFor(selected);

    bar.classList.toggle('has-selection', n > 0);
    // With a selection the bar is always useful; with none it is only useful
    // once the closest-match card and its own button have scrolled away.
    bar.hidden = !(n > 0 || scrolledPastLead);
  }

  var scrolledPastLead = false;

  function watchScroll() {
    var anchor = root.querySelector('.xr-lead') || root.querySelector('.xr-hero');
    if (!anchor || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(
      function (entries) {
        scrolledPastLead = entries[0].boundingClientRect.top < 0 && !entries[0].isIntersecting;
        syncBar();
      },
      { threshold: 0 }
    ).observe(anchor);
  }

  if (rows.length || partNumber) {
    buildBar();
    watchScroll();
    syncBar();
  }

  /* Escape closes the comparison — the only thing here that traps attention. */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && comparePanel) {
      closeCompare();
      announce('Comparison closed');
    }
  });

  /* Printing: a cross-reference often ends up attached to a purchase
   * requisition, so undo any filtering and open every disclosure first. */
  window.addEventListener('beforeprint', function () {
    rows.forEach(function (r) { r.hidden = false; });
    Array.prototype.forEach.call(root.querySelectorAll('.xr-brand'), function (g) { g.hidden = false; });
    Array.prototype.forEach.call(root.querySelectorAll('details'), function (d) {
      if (!d.open) { d.setAttribute('data-xr-reopen', '1'); d.open = true; }
    });
  });
  window.addEventListener('afterprint', function () {
    Array.prototype.forEach.call(root.querySelectorAll('details[data-xr-reopen]'), function (d) {
      d.open = false;
      d.removeAttribute('data-xr-reopen');
    });
    applyFilters();
  });
})();
